import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import cookie from 'react-cookies';
import { toast } from 'react-toastify';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import { drapcodeIcon } from '../../../common/custom/icon-library';
import {
  VIBE_DEFAULT_MODEL_ID,
  getThinkingOptionsForModel,
  resolveThinkingForModel,
} from '../../../features/vibe-coding/redux/constants';
import ModelSelector from './ModelSelector';
import ThinkingSelector from './ThinkingSelector';
import {
  ATTACH_BUTTON_STYLE,
  CARD_STATE_STYLES,
  CARD_STYLE,
  PLACEHOLDER,
  SEND_BUTTON_STYLE,
  TEXTAREA_STYLE,
} from '../utils/promptUtils';

const ChatInput = ({
  textareaRef,
  prompt,
  isFocused,
  canSubmit,
  isGenerating,
  disabled,
  placeholder,
  showAttach,
  selectedModel,
  selectedThinking,
  onModelChange,
  onThinkingChange,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onCardClick,
  onSubmit,
  onStop,
  attachments = [],
  onAttachmentsChange,
}) => {
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const urlCacheRef = useRef({});
  const fileInputRef = useRef(null);

  const handleAttachmentClick = async (attachment) => {
    if (!attachment) return;
    setSelectedAttachment(attachment);
    setModalOpen(true);

    const cleanKey = (attachment.key || attachment.name || '').split('/').pop();
    if (!cleanKey) return;

    if (urlCacheRef.current[cleanKey]) {
      setPreviewUrl(urlCacheRef.current[cleanKey]);
      setPreviewLoading(false);
      setPreviewError('');
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');
    setPreviewUrl('');

    try {
      const baseUrl = process.env.REACT_APP_BUILDER_ENGINE || 'http://localhost:5000/api/';
      const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const apiUrl = `${normalized}file/user-prompt-attachment-url/${encodeURIComponent(cleanKey)}`;

      const token = cookie.load('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const response = await axios.get(apiUrl, config);
      const fileUrl = response && response.data && response.data.url;

      if (fileUrl) {
        urlCacheRef.current[cleanKey] = fileUrl;
        setPreviewUrl(fileUrl);
      } else {
        setPreviewError('Attachment URL not returned from server.');
      }
    } catch (err) {
      const errorMsg =
        (err && err.response && err.response.data && err.response.data.message) ||
        err.message ||
        'Failed to fetch attachment URL.';
      setPreviewError(errorMsg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const fileList = e && e.target && e.target.files ? Array.from(e.target.files) : [];
    if (!fileList || fileList.length === 0) return;

    if (e.target) {
      e.target.value = '';
    }

    setIsUploading(true);
    try {
      const baseUrl = process.env.REACT_APP_BUILDER_ENGINE || 'http://localhost:5000/api/';
      const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const uploadUrl = `${normalized}file/user-prompt-attachment-public`;

      const token = cookie.load('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const uploadPromises = fileList.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(uploadUrl, formData, config);
        const data = response && response.data;
        const key = data && (data.key || (data.data && data.data.key));

        if (!key) {
          throw new Error((data && data.message) || `Failed to upload ${file.name}`);
        }

        return {
          key,
          name: file.name || key.split('/').pop(),
        };
      });

      const uploaded = await Promise.all(uploadPromises);

      if (onAttachmentsChange) {
        onAttachmentsChange([...attachments, ...uploaded]);
      }
    } catch (err) {
      console.error('Failed to upload attachment:', err);
      const errMsg =
        (err && err.response && err.response.data && err.response.data.message) ||
        err.message ||
        'Failed to upload attachment.';
      toast.error(errMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };


    const activeModel = selectedModel || VIBE_DEFAULT_MODEL_ID;
  const thinkingOptions = getThinkingOptionsForModel(activeModel);
  const activeThinking = resolveThinkingForModel(activeModel, selectedThinking);

  const selectorsDisabled = disabled || isGenerating;
  
  return (
    <div
      className="card shadow-sm"
      onClick={onCardClick}
      style={{ ...CARD_STYLE, ...CARD_STATE_STYLES[isFocused ? 'focused' : 'idle'] }}
    >
      <textarea
        ref={textareaRef}
        rows={3}
        placeholder={placeholder}
        value={prompt}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        readOnly={isGenerating}
        disabled={disabled}
        style={TEXTAREA_STYLE}
        autoFocus
        aria-label="Describe what to build"
      />

      {attachments.length > 0 && (
        <div
          className="d-flex flex-wrap align-items-center"
          style={{ gap: '8px', marginTop: '8px' }}
          aria-label="Attachments"
        >
          {attachments.map((attachment, idx) => {
            const isHovered = hoveredKey === (attachment.key || idx);
            return (
              <div
                key={attachment.key || idx}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAttachmentClick(attachment);
                }}
                onMouseEnter={() => setHoveredKey(attachment.key || idx)}
                onMouseLeave={() => setHoveredKey(null)}
                className="d-flex align-items-center"
                style={{
                  border: isHovered ? '1px solid #94a3b8' : '1px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: '4px 8px 4px 8px',
                  background: isHovered ? '#edf2f7' : '#f8f9fa',
                  maxWidth: '240px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                }}
                title={`Click to preview ${attachment.name}`}
              >
                <i
                  className="wb webconnect-eye mr-1 text-muted"
                  style={{ fontSize: '13px', opacity: isHovered ? 1 : 0.7 }}
                />
                <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: isHovered ? '#0f172a' : '#212529',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={attachment.name}
                  >
                    {attachment.name}
                  </div>
                </div>
                {onAttachmentsChange && !isGenerating && (
                  <button
                    type="button"
                    className="btn btn-link text-muted p-0 ml-1 d-inline-flex align-items-center justify-content-center"
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      lineHeight: 1,
                      textDecoration: 'none',
                      color: '#64748b',
                      width: '16px',
                      height: '16px',
                    }}
                    aria-label="Remove attachment"
                    title="Remove attachment"
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = attachments.filter((_, i) => i !== idx);
                      onAttachmentsChange(next);
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          borderTop: '1px solid #eef2f7',
          marginTop: '10px',
          paddingTop: '10px',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: '8px', flexWrap: 'wrap' }}>
          {showAttach ? (
          <>
            <button
              type="button"
              className="btn btn-link d-inline-flex align-items-center justify-content-center text-muted p-0"
              title={isUploading ? 'Uploading...' : 'Attach file'}
              aria-label="Attach file"
              disabled={isGenerating || isUploading || disabled}
              style={{
                ...ATTACH_BUTTON_STYLE,
                opacity: isGenerating || disabled ? 0.5 : 1,
                cursor: isGenerating || isUploading || disabled ? 'not-allowed' : 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
            >
              {isUploading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ width: '14px', height: '14px' }}
                />
              ) : (
                <i className="wb webconnect-paperclip" style={{ fontSize: '16px' }} />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </>
        ) : (
          <span />
        )}
          <ModelSelector
            value={activeModel}
            onChange={onModelChange}
            disabled={selectorsDisabled}
          />
          <ThinkingSelector
            value={activeThinking}
            options={thinkingOptions}
            onChange={onThinkingChange}
            disabled={selectorsDisabled}
          />
        </div>

        {isGenerating ? (
          <button
            type="button"
            className="btn btn-theme-submit d-inline-flex align-items-center justify-content-center"
            onClick={(e) => {
              e.stopPropagation();
              onStop();
            }}
            title="Stop generation"
            aria-label="Stop generation"
            style={SEND_BUTTON_STYLE}
          >
            <span
              aria-hidden="true"
              style={{
                width: '10px',
                height: '10px',
                background: 'currentColor',
                borderRadius: '2px',
              }}
            />
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-theme-submit d-inline-flex align-items-center justify-content-center"
            disabled={!canSubmit || isUploading}
            onClick={(e) => {
              e.stopPropagation();
              onSubmit();
            }}
            title={isUploading ? 'Uploading attachments...' : 'Build app'}
            aria-label="Build app"
            style={SEND_BUTTON_STYLE}
          >
            <i
              className={`wb ${drapcodeIcon('send')}`}
              style={{ fontSize: '15px' }}
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      <AttachmentPreviewModal
        isOpen={modalOpen}
        toggle={closeModal}
        attachment={selectedAttachment}
        url={previewUrl}
        isLoading={previewLoading}
        error={previewError}
      />
    </div>
  );
};

ChatInput.propTypes = {
  textareaRef: PropTypes.object,
  prompt: PropTypes.string.isRequired,
  isFocused: PropTypes.bool,
  canSubmit: PropTypes.bool,
  isGenerating: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  showAttach: PropTypes.bool,
  selectedModel: PropTypes.string,
  selectedThinking: PropTypes.string,
  onModelChange: PropTypes.func,
  onThinkingChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onCardClick: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onStop: PropTypes.func,
  attachments: PropTypes.array,
  onAttachmentsChange: PropTypes.func,
};

ChatInput.defaultProps = {
  attachments: [],
  disabled: false,
  placeholder: PLACEHOLDER,
  showAttach: true,
  selectedModel: VIBE_DEFAULT_MODEL_ID,
  selectedThinking: undefined,
  onModelChange: null,
  onThinkingChange: null,
};

export default ChatInput;
